import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { authApi } from '../../lib/api';

/**
 * Modaler, nicht schließbarer Gate, der eingeloggte Teilnehmer:innen ohne
 * vollständige Stammdaten (Geburtsdatum, Straße, PLZ, Ort) zur Vervollständigung
 * zwingt. Auslöser: seit 05/2026 sind diese Felder Pflicht (Lizenz + Rechnung);
 * Bestandsmitglieder mit fehlenden Werten werden hier abgefangen.
 *
 * Eingebaut in ParticipantPortalPage – damit greift der Gate auf der ersten
 * Seite nach Login. Solange der Gate offen ist, ist der dahinterliegende
 * Portal-Inhalt blockiert (Dialog-Backdrop) und ein Logout-Schalter steht
 * zur Verfügung, falls jemand die Daten nicht angeben möchte.
 */
export function ProfileCompletionGate() {
    const { user, checkAuth, logout } = useAuth();
    const [submitting, setSubmitting] = useState(false);
    const [form, setForm] = useState({
        birth_date: '',
        street: '',
        postal_code: '',
        city: '',
    });

    // Gate nur für Teilnehmer:innen (nicht Admins). Bedingung: ein
    // Pflichtfeld fehlt.
    const isParticipant = user && user.role !== 'admin' && user.role !== 'super_admin';
    const incomplete = isParticipant && (
        !user.birth_date || !user.street || !user.postal_code || !user.city
    );

    // Beim ersten Öffnen vorhandene Werte vorbelegen (falls Teile schon gepflegt).
    React.useEffect(() => {
        if (incomplete) {
            setForm((prev) => ({
                birth_date: prev.birth_date || user.birth_date || '',
                street: prev.street || user.street || '',
                postal_code: prev.postal_code || user.postal_code || '',
                city: prev.city || user.city || '',
            }));
        }
    }, [incomplete, user]);

    if (!incomplete) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.birth_date || !form.street.trim() || !form.postal_code.trim() || !form.city.trim()) {
            toast.error('Bitte füllen Sie alle Pflichtfelder aus');
            return;
        }
        setSubmitting(true);
        try {
            await authApi.updateProfile({
                birth_date: form.birth_date,
                street: form.street.trim(),
                postal_code: form.postal_code.trim(),
                city: form.city.trim(),
            });
            await checkAuth();
            toast.success('Vielen Dank – Ihre Stammdaten sind nun vollständig');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open onOpenChange={() => { /* nicht schließbar */ }}>
            <DialogContent
                className="max-w-md [&>button.absolute]:hidden"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                onInteractOutside={(e) => e.preventDefault()}
                data-testid="profile-completion-gate"
            >
                <DialogHeader>
                    <div className="flex items-center gap-2 text-primary">
                        <AlertCircle className="h-5 w-5" />
                        <DialogTitle>Profil vervollständigen</DialogTitle>
                    </div>
                    <DialogDescription className="pt-2 text-sm leading-relaxed">
                        Bevor Sie eine Fortbildung buchen können, benötigen wir noch Ihre Anschrift
                        und Ihr Geburtsdatum. Diese Angaben werden für die Ausstellung Ihrer Lizenz
                        und für die Rechnungsstellung benötigt.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-3 pt-2">
                    <div className="space-y-1.5">
                        <Label htmlFor="gate_birth_date">Geburtsdatum *</Label>
                        <Input
                            id="gate_birth_date"
                            type="date"
                            max={new Date().toISOString().split('T')[0]}
                            value={form.birth_date}
                            onChange={(e) => setForm({ ...form, birth_date: e.target.value })}
                            required
                            data-testid="gate-birth-date"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <Label htmlFor="gate_street">Straße und Hausnummer *</Label>
                        <Input
                            id="gate_street"
                            placeholder="Musterstraße 1"
                            value={form.street}
                            onChange={(e) => setForm({ ...form, street: e.target.value })}
                            required
                            data-testid="gate-street"
                        />
                    </div>
                    <div className="grid grid-cols-[120px_1fr] gap-3">
                        <div className="space-y-1.5">
                            <Label htmlFor="gate_postal_code">PLZ *</Label>
                            <Input
                                id="gate_postal_code"
                                placeholder="10115"
                                value={form.postal_code}
                                onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                                required
                                inputMode="numeric"
                                pattern="[0-9]{4,5}"
                                data-testid="gate-postal-code"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="gate_city">Ort *</Label>
                            <Input
                                id="gate_city"
                                placeholder="Berlin"
                                value={form.city}
                                onChange={(e) => setForm({ ...form, city: e.target.value })}
                                required
                                data-testid="gate-city"
                            />
                        </div>
                    </div>
                    <div className="flex flex-col-reverse sm:flex-row gap-2 pt-3">
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={logout}
                            disabled={submitting}
                            className="sm:w-auto w-full text-xs"
                            data-testid="gate-logout"
                        >
                            Später – jetzt abmelden
                        </Button>
                        <Button
                            type="submit"
                            disabled={submitting}
                            className="sm:flex-1 w-full"
                            data-testid="gate-submit"
                        >
                            {submitting ? (
                                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Wird gespeichert…</>
                            ) : (
                                'Stammdaten speichern'
                            )}
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
}

export default ProfileCompletionGate;
